import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Product } from '../../types/product';
import { products } from '../../data/products';
import { useCart } from '../../context/CartContext';
import { createSlug } from '../../utils/slug';
import './ProductDetail.css';

interface ProductDetailProps {
  onAddToCart?: () => void;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ onAddToCart }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>('description');
  const [backgroundPosition, setBackgroundPosition] = useState('center center');
  const [sizeError, setSizeError] = useState<string>('');
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const foundProduct = products.find(p => createSlug(p.name) === slug);
    if (foundProduct) {
      setProduct(foundProduct);
      // Chỉ hiển thị 1 hình
      setProductImages([foundProduct.image]);
    }
  }, [slug]);

  // Parallax effect cho banner
  useEffect(() => {
    const handleScroll = () => {
      if (bannerRef.current) {
        const rect = bannerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const sectionTop = rect.top;
        const sectionHeight = rect.height;
        
        if (sectionTop < windowHeight && sectionTop + sectionHeight > 0) {
          const scrollProgress = Math.max(0, Math.min(1, (windowHeight - sectionTop) / (windowHeight + sectionHeight)));
          const yPos = scrollProgress * 20; // Adjust 20 for parallax speed
          setBackgroundPosition(`center ${50 + yPos}%`); // Reverse parallax
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); 
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [productImages, setProductImages] = useState<string[]>([]);

  const sizes = ['S', 'M', 'XL', 'L'];
  const colors = ['Pink', 'Plum', 'Tan', 'Steelblue', 'Red', 'Coffee', 'White', 'Peru', 'Teal'];
  const materials = ['Cotton', 'Taffeta', 'Silk', 'Nylon', 'Polyester', 'Tulle', 'Velvet', 'Wool', 'Wool Blend'];

  if (!product) {
    return (
      <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
        <h2>Sản phẩm không tìm thấy</h2>
        <button onClick={() => navigate('/')} className="btn-primary">Về trang chủ</button>
      </div>
    );
  }

  const handleQuantityChange = (delta: number) => {
    setQuantity(Math.max(1, quantity + delta));
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // Kiểm tra bắt buộc phải chọn size
    if (!selectedSize) {
      setSizeError('Vui lòng chọn kích thước trước khi thêm vào giỏ hàng');
      return;
    }
    
    setSizeError(''); // Xóa error nếu đã chọn size
    addToCart({
      product,
      quantity,
      selectedSize,
      selectedColor,
      selectedMaterial,
    });
    
    if (onAddToCart) {
      onAddToCart();
    }
  };

  const handleBuyNow = () => {
    if (!product) return;
    
    // Kiểm tra bắt buộc phải chọn size
    if (!selectedSize) {
      setSizeError('Vui lòng chọn kích thước trước khi mua hàng');
      return;
    }
    
    setSizeError(''); // Xóa error nếu đã chọn size
    addToCart({
      product,
      quantity,
      selectedSize,
      selectedColor,
      selectedMaterial,
    });
    
    navigate('/checkout');
  };

  const handleSizeSelect = (size: string) => {
    setSelectedSize(size);
    setSizeError(''); // Xóa error khi chọn size
  };

  return (
    <div className="product-detail-page">
        {/* Product Banner */}
        <div className="product-banner" ref={bannerRef}>
          <div 
            className="product-banner-background"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/background/bg_route_product.jpg)`,
              backgroundPosition: backgroundPosition,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            }}
          ></div>
          <div className="product-banner-overlay"></div>
          <div className="product-banner-top">
            <div className="container">
              <h1 className="product-banner-title">PRODUCT</h1>
              <div className="product-banner-breadcrumb">
                <a href="/">Tất cả</a> / <span>{product.name}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="product-detail-content">
            {/* Product Gallery */}
            <div className="product-gallery">
              <div className="product-gallery-main">
                <img 
                  src={productImages[selectedImageIndex] || product.image} 
                  alt={product.name}
                />
              </div>
              <div className="product-gallery-thumbnails">
                {productImages.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="product-info">
              <h1 className="product-title">{product.name}</h1>
              
              <div className="product-price-section">
                <span className="product-price-label">Giá:</span>
                <div className="product-prices">
                  {product.isSale && product.oldPrice ? (
                    <>
                      <span className="product-price-current">{product.price.toLocaleString('vi-VN')}đ</span>
                      <span className="product-price-old">{product.oldPrice.toLocaleString('vi-VN')}đ</span>
                    </>
                  ) : (
                    <span className="product-price-current">{product.price.toLocaleString('vi-VN')}đ</span>
                  )}
                </div>
              </div>

              {/* Variants */}
              <div className="product-variants">
                <div className="variant-group">
                  <label>Kích thước</label>
                  <div className="variant-options">
                    {sizes.map(size => (
                      <button
                        key={size}
                        className={`variant-btn ${selectedSize === size ? 'selected' : ''}`}
                        onClick={() => handleSizeSelect(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  {sizeError && <div className="size-error-message">{sizeError}</div>}
                </div>

              </div>

              {/* Selected Variants Display */}
              {selectedSize && (
                <div className="selected-variants">
                  <span>S / {selectedSize}</span>
                </div>
              )}

              {/* Product Meta */}
              <div className="product-meta">
                <div className="meta-item">
                  <strong>Nhà cung cấp:</strong> <span>Mơ Nails</span>
                </div>
                <div className="meta-item">
                  <strong>Loại:</strong> <span>Nail Box</span>
                </div>
                <div className="meta-item">
                  <strong>Tình trạng:</strong> <span>Còn hàng</span>
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="product-actions">
                <div className="quantity-selector">
                  <button onClick={() => handleQuantityChange(-1)} className="quantity-btn">-</button>
                  <input 
                    type="number" 
                    value={quantity} 
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                  />
                  <button onClick={() => handleQuantityChange(1)} className="quantity-btn">+</button>
                </div>
                <div className="action-buttons">
                  <button onClick={handleAddToCart} className="btn-add-to-cart">Thêm vào giỏ</button>
                  <button onClick={handleBuyNow} className="btn-buy-now">Mua ngay</button>
                </div>
              </div>

              {/* Buy it now & Add to Cart */}
              <div className="product-buy-now">
                <button onClick={handleAddToCart} className="btn-add-to-cart-secondary">Thêm vào giỏ</button>
                <button onClick={handleBuyNow} className="btn-buy-now">Mua ngay</button>
              </div>

              {/* Tabs */}
              <div className="product-tabs">
                <button 
                  className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
                  onClick={() => setActiveTab('description')}
                >
                  Mô tả sản phẩm
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'information' ? 'active' : ''}`}
                  onClick={() => setActiveTab('information')}
                >
                  Thông tin bổ sung
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                  onClick={() => setActiveTab('reviews')}
                >
                  Đánh giá
                </button>
              </div>

              {/* Tab Content */}
              <div className="product-tab-content">
                {activeTab === 'description' && (
                  <div className="tab-panel">
                    {product.description ? (
                      <p>{product.description}</p>
                    ) : (
                      <p>Móng giả chất lượng cao, thiết kế đẹp mắt, phù hợp cho mọi dịp. Sản phẩm được làm từ chất liệu an toàn, không gây kích ứng, dễ dàng gắn và tháo mà không làm hỏng móng thật.</p>
                    )}
                  </div>
                )}
                {activeTab === 'information' && (
                  <div className="tab-panel">
                    <h4>Chính sách đổi trả</h4>
                    <p>Bạn có thể trả lại hầu hết các sản phẩm mới, chưa mở trong vòng 30 ngày kể từ ngày nhận hàng để được hoàn tiền đầy đủ. Chúng tôi cũng sẽ thanh toán phí vận chuyển đổi trả nếu việc đổi trả là do lỗi của chúng tôi.</p>
                    <h4>Vận chuyển</h4>
                    <p>Chúng tôi có thể giao hàng đến hầu hết mọi địa chỉ. Lưu ý rằng có một số hạn chế đối với một số sản phẩm, và một số sản phẩm không thể giao hàng quốc tế.</p>
                  </div>
                )}
                {activeTab === 'reviews' && (
                  <div className="tab-panel">
                    <h4>Đánh giá khách hàng</h4>
                    <p>Dựa trên 1 đánh giá</p>
                    <button className="btn-write-review">Viết đánh giá</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recently Viewed Products */}
          <div className="recently-viewed-section">
            <h3>Sản phẩm đã xem gần đây</h3>
            <div className="recently-viewed-grid">
              {products.slice(0, 4).map((p) => (
                <div key={p.id} className="recently-viewed-item" onClick={() => navigate(`/product/${createSlug(p.name)}`)}>
                  <img src={p.image} alt={p.name} />
                  <h4>{p.name}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
};

export default ProductDetail;

